import { useEffect, useRef, useState } from "react";
import { messaging } from "../services/messaging.js";
import { getAttachmentBlob, type Message } from "../storage/db.js";

/** m:ss, for a clip length or a play position. */
export function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
}

/** Only one voice note plays at a time. */
let currentlyPlaying: HTMLAudioElement | null = null;

/**
 * A received or sent voice message: play/pause, a progress bar and the length.
 * Unlike a photo it can be replayed — the clip lives until the message
 * self-destructs on the usual 5-minute timer.
 */
export function VoiceBubble({ m }: { m: Message }) {
  const info = m.attachment;
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [error, setError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      // Leaving the chat: stop and release the clip from memory.
      if (audioRef.current) {
        audioRef.current.pause();
        if (currentlyPlaying === audioRef.current) currentlyPlaying = null;
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
      audioRef.current = null;
    },
    [],
  );

  const durationMs = info?.durationMs ?? 0;
  const gone = !info || (m.dir === "out" && info.viewed);

  async function toggle() {
    if (!info) return;
    const existing = audioRef.current;
    if (existing && !existing.paused) {
      existing.pause();
      setPlaying(false);
      return;
    }
    if (existing) {
      void existing.play().catch(() => setError(true));
      return;
    }

    const bytes = await getAttachmentBlob(m.id);
    if (!bytes) {
      setError(true);
      return;
    }
    const blobPart = new Uint8Array(bytes).buffer as ArrayBuffer;
    const url = URL.createObjectURL(new Blob([blobPart], { type: info.mimeType || "audio/mp4" }));
    urlRef.current = url;
    const audio = new Audio(url);
    audio.preload = "auto";
    audio.addEventListener("timeupdate", () => setPositionMs(audio.currentTime * 1000));
    audio.addEventListener("ended", () => {
      setPlaying(false);
      setPositionMs(0);
      audio.currentTime = 0;
      if (currentlyPlaying === audio) currentlyPlaying = null;
    });
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("play", () => setPlaying(true));
    audio.addEventListener("error", () => setError(true));
    audioRef.current = audio;

    currentlyPlaying?.pause();
    currentlyPlaying = audio;
    try {
      await audio.play();
      // Tells the sender it was heard (their copy goes); ours stays replayable.
      void messaging.markVoiceHeard(m.id, m.peerUserId);
    } catch {
      setError(true);
    }
  }

  if (gone) return <div className="attachment-card gone">🎤 Voice message · played</div>;

  const progress = durationMs > 0 ? Math.min(100, (positionMs / durationMs) * 100) : 0;
  const shown = playing || positionMs > 0 ? positionMs : durationMs;

  return (
    <div className="voice">
      <button
        className="voice-play"
        onClick={() => void toggle()}
        aria-label={playing ? "Pause voice message" : "Play voice message"}
      >
        {playing ? "⏸" : "▶︎"}
      </button>
      <div className="voice-track" aria-hidden>
        <div className="voice-progress" style={{ width: `${progress}%` }} />
      </div>
      <span className="voice-time">{error ? "unplayable" : fmtDuration(shown)}</span>
    </div>
  );
}
